import { useCallback, useEffect, useState } from "react";
import {
    createAdminNewsCard,
    deleteAdminNewsCard,
    fetchAdminNewsCards,
    updateAdminNewsCard,
    type NewsCard,
    type NewsCardInput,
} from "../api/tcg";

type EditableNewsCard = NewsCardInput & {
    id?: number;
};

const EMPTY_CARD: EditableNewsCard = {
    position: 1,
    is_visible: true,
    kind: "instagram",
    title: "Instagram",
    description: "",
    image_url: "",
    link_url: "",
    link_label: "",
    instagram_url: "",
};

function normalizeOptionalText(value: string | null | undefined): string | null {
    const trimmed = value?.trim() ?? "";
    return trimmed === "" ? null : trimmed;
}

function isValidInstagramUrl(value: string): boolean {
    return /^https?:\/\/(www\.)?instagram\.com\/(p|reel|reels|tv)\/[A-Za-z0-9_-]+/i.test(value.trim());
}

function getExistingInstagramUrl(card: NewsCard): string {
    const possibleUrls = [
        card.instagram_url,
        card.image_url,
        card.link_url,
    ];

    return possibleUrls.find((url) => url && isValidInstagramUrl(url)) ?? "";
}

function toEditableCard(card: NewsCard): EditableNewsCard {
    return {
        id: card.id,
        position: card.position,
        is_visible: card.is_visible,
        kind: "instagram",
        title: "Instagram",
        description: "",
        image_url: "",
        link_url: "",
        link_label: "",
        instagram_url: getExistingInstagramUrl(card),
    };
}

function toPayload(card: EditableNewsCard): NewsCardInput {
    return {
        position: Number(card.position),
        is_visible: card.is_visible,
        kind: "instagram",
        title: "Instagram",
        description: null,
        image_url: null,
        link_url: null,
        link_label: null,
        instagram_url: normalizeOptionalText(card.instagram_url),
    };
}

function validateCard(card: EditableNewsCard): string | null {
    if (!Number.isInteger(Number(card.position)) || Number(card.position) < 1) {
        return "Die Position muss eine positive Zahl sein.";
    }

    if (!isValidInstagramUrl(card.instagram_url ?? "")) {
        return "Bitte eine gültige Instagram-Beitrags-URL angeben (z. B. https://www.instagram.com/p/...).";
    }

    return null;
}

function AdminNewsCards({ token }: { token: string }) {
    const [cards, setCards] = useState<EditableNewsCard[]>([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState<number | "new" | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const items = await fetchAdminNewsCards(token);
            setCards(items.sort((a, b) => a.position - b.position).map(toEditableCard));
        } catch (err) {
            setError(err instanceof Error ? err.message : "Kacheln konnten nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { void load(); }, [load]);

    function updateCard(index: number, patch: Partial<EditableNewsCard>) {
        setCards((current) =>
            current.map((card, i) => i === index ? { ...card, ...patch } : card)
        );
        setMessage(null);
        setError(null);
    }

    async function handleSave(index: number) {
        const card = cards[index];
        const validationError = validateCard(card);
        if (validationError) {
            setMessage(null);
            setError(validationError);
            return;
        }

        setBusyId(card.id ?? "new");
        setMessage(null);
        setError(null);
        try {
            if (card.id) {
                await updateAdminNewsCard(token, card.id, toPayload(card));
                setMessage("Kachel gespeichert.");
            } else {
                await createAdminNewsCard(token, toPayload(card));
                setMessage("Kachel erstellt.");
            }
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Kachel konnte nicht gespeichert werden.");
        } finally {
            setBusyId(null);
        }
    }

    async function handleDelete(card: EditableNewsCard) {
        if (!card.id) {
            setCards((current) => current.filter((item) => item !== card));
            return;
        }

        const confirmed = window.confirm("Diese Kachel wirklich löschen?");
        if (!confirmed) return;

        setBusyId(card.id);
        setMessage(null);
        setError(null);
        try {
            await deleteAdminNewsCard(token, card.id);
            setMessage("Kachel gelöscht.");
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Kachel konnte nicht gelöscht werden.");
        } finally {
            setBusyId(null);
        }
    }

    function addCard() {
        const maxPosition = cards.reduce((max, card) => Math.max(max, Number(card.position) || 0), 0);
        setCards((current) => [
            ...current,
            { ...EMPTY_CARD, position: maxPosition + 1 },
        ]);
        setMessage(null);
        setError(null);
    }

    const busy = busyId !== null;

    return (
        <section className="admin-card">
            <h2 className="admin-card-title">News- &amp; Social-Kacheln bearbeiten</h2>
            <p className="admin-note" style={{ marginBottom: "18px" }}>
                Hier können die Kacheln im News-&amp;-Socials-Bereich des Onepagers gepflegt werden.
                Pro Kachel reicht die öffentliche Beitrags-, Reel- oder IGTV-URL.
            </p>

            {message && <p className="admin-success">{message}</p>}
            {error && <p className="admin-error">{error}</p>}
            {loading && <p className="admin-note">Kacheln werden geladen...</p>}

            {!loading && cards.length === 0 && (
                <div className="admin-blocklist-empty">
                    <p className="admin-note">Aktuell sind keine Kacheln angelegt.</p>
                </div>
            )}

            {cards.map((card, index) => {
                const cardBusy = busyId === (card.id ?? "new");

                return (
                    <div className="admin-action-block admin-news-card-editor" key={card.id ?? `new-${index}`}>
                        <div className="admin-news-card-preview">
                            <div className="admin-news-card-preview-image">
                                <span>Social-Embed</span>
                            </div>
                            <div>
                                <span className="admin-news-card-status">
                                    {card.is_visible ? "Sichtbar" : "Versteckt"} - Position {card.position || "-"}
                                </span>
                                <h3>Social-Beitrag</h3>
                                <p>{card.instagram_url?.trim() || "Noch keine URL eingetragen."}</p>
                            </div>
                        </div>

                        <div className="admin-news-card-form">
                            <label className="admin-news-card-checkbox">
                                <input
                                    type="checkbox"
                                    checked={card.is_visible}
                                    onChange={(event) => updateCard(index, { is_visible: event.target.checked })}
                                    disabled={busy}
                                />
                                Sichtbar
                            </label>

                            <div>
                                <label className="admin-label">Position</label>
                                <input
                                    className="admin-input"
                                    type="number"
                                    min={1}
                                    value={card.position}
                                    onChange={(event) => updateCard(index, { position: Number(event.target.value) })}
                                    disabled={busy}
                                />
                            </div>

                            <div style={{ gridColumn: "1 / -1" }}>
                                <label className="admin-label">Instagram-URL</label>
                                <input
                                    className="admin-input"
                                    value={card.instagram_url ?? ""}
                                    onChange={(event) => updateCard(index, { instagram_url: event.target.value })}
                                    placeholder="https://www.instagram.com/p/..."
                                    disabled={busy}
                                />
                                <p className="admin-label" style={{ opacity: 0.75, marginTop: "6px" }}>
                                    Oeffentlichen Beitrags-, Reel- oder IGTV-Link einfuegen. Bild und Text werden direkt von Instagram angezeigt.
                                </p>
                            </div>
                        </div>

                        <div className="admin-news-card-actions">
                            <button
                                className="admin-btn-primary"
                                type="button"
                                onClick={() => void handleSave(index)}
                                disabled={busy}
                            >
                                {cardBusy ? "Speichert..." : "Speichern"}
                            </button>
                            <button
                                className="admin-btn-secondary admin-btn-muted-danger"
                                type="button"
                                onClick={() => void handleDelete(card)}
                                disabled={busy}
                            >
                                Löschen
                            </button>
                        </div>
                    </div>
                );
            })}

            <div style={{ marginTop: "22px" }}>
                <button className="admin-btn-secondary" type="button" onClick={addCard} disabled={busy || loading}>
                    Neue Kachel hinzufügen
                </button>
            </div>
        </section>
    );
}

export default AdminNewsCards;
